import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import CouponCard from "@/components/CouponCard";
import PageBackground from "@/components/PageBackground";
import AIPreferenceCard from "@/components/AIPreferenceCard";
import { Bookmark, Star, Bell, Gift, Wallet, ArrowRight } from "lucide-react";

export default function UserDashboard() {
  const [data, setData] = useState(null);
  const [codes, setCodes] = useState([]);
  const [wallet, setWallet] = useState(null);
  useEffect(() => {
    api.get("/user/dashboard").then((r) => setData(r.data));
    api.get("/user/coupons").then((r) => setCodes(r.data));
    api.get("/wallet/me").then((r) => setWallet(r.data)).catch(() => {});
  }, []);
  if (!data) return <div className="relative"><PageBackground variant="portal" /><div className="max-w-7xl mx-auto px-6 py-12 relative z-10 text-slate-500">Loading...</div></div>;
  const k = wallet?.kpis || { available_cashback: 0, pending_cashback: 0, total_savings: 0 };
  return (
    <div className="relative">
      <PageBackground variant="portal" />
      <div className="max-w-7xl mx-auto px-6 py-12 relative z-10" data-testid="user-dashboard">
      <div className="mb-10">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Your account</div>
        <h1 className="font-display text-5xl font-semibold tracking-tighter mt-2">Hi, {data.user.name} 👋</h1>
      </div>

      <div className="pf-card p-6 md:p-8 mb-8 bg-gradient-to-br from-cyan-50 to-emerald-50 ring-1 ring-cyan-200" data-testid="dashboard-wallet-card">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="pf-chip pf-chip-mint w-fit"><Wallet className="w-3 h-3" /> Cashback wallet</div>
            <div className="grid grid-cols-3 gap-6 mt-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Available</div>
                <div className="font-display text-2xl md:text-3xl font-medium mt-1 pf-gradient-text-2 tabular-nums" data-testid="dashboard-wallet-available">CAD ${(k.available_cashback || 0).toFixed(2)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Pending</div>
                <div className="font-display text-2xl md:text-3xl font-medium mt-1 tabular-nums text-amber-600" data-testid="dashboard-wallet-pending">CAD ${(k.pending_cashback || 0).toFixed(2)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Lifetime savings</div>
                <div className="font-display text-2xl md:text-3xl font-medium mt-1 tabular-nums" data-testid="dashboard-wallet-lifetime">CAD ${(k.total_savings || 0).toFixed(2)}</div>
              </div>
            </div>
          </div>
          <Link to="/wallet" className="pf-btn-primary px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1" data-testid="dashboard-wallet-cta">
            View wallet <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
        {[
          { icon: Bookmark, label: "Saved coupons", value: data.saved_coupons.length },
          { icon: Star, label: "Favorite stores", value: data.favorite_stores.length },
          { icon: Bell, label: "Notifications", value: "On" },
          { icon: Gift, label: "Referrals", value: "0" },
        ].map((s, i) => (
          <div key={i} className="pf-card p-5">
            <s.icon className="w-4 h-4 text-muted-foreground" />
            <div className="font-display text-3xl font-semibold mt-3">{s.value}</div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      <section className="mb-16">
        <h2 className="font-display text-3xl font-semibold tracking-tight mb-6">Your CYN codes ({codes.length})</h2>
        {codes.length === 0 ? (
          <div className="pf-card p-8 text-center text-slate-500" data-testid="no-codes">
            Claim a campaign from the marketplace and your unique <span className="font-mono text-cyan-700">CYN-…</span> codes will appear here.
          </div>
        ) : (
          <div className="pf-card overflow-hidden" data-testid="my-codes-table">
            <table className="w-full text-sm">
              <thead className="bg-white/60 text-left text-xs uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-5 py-3">Code</th>
                  <th className="px-5 py-3">Campaign</th>
                  <th className="px-5 py-3">Brand</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Issued</th>
                </tr>
              </thead>
              <tbody>
                {codes.map((c) => {
                  return (
                  <tr key={c.id} className="border-t border-white/60" data-testid={`my-code-${c.id}`}>
                    <td className="px-5 py-3 font-mono font-semibold text-slate-900">{c.code_ready === false ? <span className="text-amber-700 italic font-sans text-sm" data-testid={`my-code-pending-${c.id}`}>{c.shopify_sync_status === "SHOPIFY_SYNC_FAILED" ? <Link to={`/coupon/${c.campaign_id}`} className="underline">Activation failed — open the offer to retry</Link> : "Activating on store…"}</span> : (c.code || <span className="text-slate-400 italic">auto-apply</span>)}</td>
                    <td className="px-5 py-3 text-slate-700 truncate">{c.campaign_name}</td>
                    <td className="px-5 py-3 text-slate-700"><Link to={`/store/${c.store_slug}`} className="hover:text-cyan-700">{c.store_name}</Link></td>
                    <td className="px-5 py-3"><span className={`pf-chip ${c.status === "redeemed" ? "pf-chip-mint" : c.status === "validated" ? "pf-chip-cyan" : ""}`}>{c.status}</span></td>
                    <td className="px-5 py-3 text-xs text-slate-500">{new Date(c.created_at).toLocaleDateString()}</td>
                  </tr>
                );})}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="mb-16">
        <h2 className="font-display text-3xl font-semibold tracking-tight mb-6">Saved coupons</h2>
        {data.saved_coupons.length === 0 ? (
          <div className="pf-card p-8 text-center text-muted-foreground">
            You haven't saved anything yet. <Link to="/coupons" className="underline">Browse coupons</Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.saved_coupons.map((c) => <CouponCard key={c.id} coupon={c} saved />)}
          </div>
        )}
      </section>

      <section>
        <h2 className="font-display text-3xl font-semibold tracking-tight mb-6">Favorite stores</h2>
        {data.favorite_stores.length === 0 ? (
          <div className="pf-card p-8 text-center text-muted-foreground">
            <Link to="/stores" className="underline">Discover stores</Link> to favorite.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {data.favorite_stores.map((s) => (
              <Link to={`/store/${s.slug}`} key={s.id} className="pf-card p-5 flex items-center gap-4">
                <img src={s.logo_url} alt={s.name} className="w-12 h-12 rounded-md border border-border bg-white object-contain" data-logo="1" />
                <div className="font-display text-lg font-medium">{s.name}</div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section className="mt-12 max-w-md">
        <AIPreferenceCard />
      </section>
      </div>
    </div>
  );
}
